import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sun, Palmtree, Heart } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const CostaCalida = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language, t } = useLanguage();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/xml/properties?limit=200&region=Costa Calida&lang=${language}`)
      .then(res => res.json())
      .then(data => {
        setProperties(data.slice(0, 9));
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [language]);

  return (
    <div style={styles.page}>
      <SEO titleKey="seo.costaCalidaTitle" descriptionKey="seo.costaCalidaDescription" />
      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle} data-testid="costa-calida-title">{t('regions.costaCalida.title')}</h1>
          <p style={styles.heroSubtitle}>{t('regions.costaCalida.subtitle')}</p>
        </div>
      </div>

      {/* Content */}
      <div style={styles.container}>
        {/* Intro */}
        <section style={styles.introSection}>
          <div style={styles.introContent}>
            <h2 style={styles.sectionTitle}>{language === 'nl' ? 'Ontdek Costa Calida' : 'Discover Costa Calida'}</h2>
            <p style={styles.text}>{t('regions.costaCalida.intro')}</p>
          </div>

          {/* Benefits */}
          <div style={styles.benefitsGrid}>
            <div style={styles.benefitCard}>
              <Sun size={32} color="#C5A059" />
              <h3 style={styles.benefitTitle}>{t('regions.costaCalida.benefit1')}</h3>
              <p style={styles.benefitText}>{t('regions.costaCalida.benefit1Text')}</p>
            </div>
            <div style={styles.benefitCard}>
              <Palmtree size={32} color="#C5A059" />
              <h3 style={styles.benefitTitle}>{t('regions.costaCalida.benefit2')}</h3>
              <p style={styles.benefitText}>{t('regions.costaCalida.benefit2Text')}</p>
            </div>
            <div style={styles.benefitCard}>
              <Heart size={32} color="#C5A059" />
              <h3 style={styles.benefitTitle}>{t('regions.costaCalida.benefit3')}</h3>
              <p style={styles.benefitText}>{t('regions.costaCalida.benefit3Text')}</p>
            </div>
          </div>
        </section>

        {/* Properties */}
        <section style={styles.propertiesSection}>
          <h2 style={styles.sectionTitle}>{t('regions.propertiesIn')} Costa Calida</h2>
          {loading ? (
            <p style={styles.loadingText}>{t('common.loading')}</p>
          ) : properties.length > 0 ? (
            <div style={styles.grid}>
              {properties.map((property, idx) => (
                <PropertyCard key={property.id} property={property} priority={idx < 3} />
              ))}
            </div>
          ) : (
            <p style={styles.emptyText}>{t('regions.noPropertiesRegion')}</p>
          )}

          <div style={styles.ctaCenter}>
            <Link to="/woningen" style={styles.btnPrimary} data-testid="view-all-button">
              {t('home.viewAllProperties')}
              <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh' },
  hero: {
    position: 'relative',
    height: '60vh',
    minHeight: '400px',
    backgroundImage: 'url(https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?crop=entropy&cs=srgb&fm=jpg&q=85)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(26, 25, 24, 0.4)',
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
    textAlign: 'center',
    color: 'white',
  },
  heroTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(3rem, 6vw, 5rem)',
    marginBottom: '1rem',
    fontWeight: 400,
  },
  heroSubtitle: { fontSize: '1.25rem', fontWeight: 300 },
  container: { maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' },
  introSection: { padding: '6rem 0' },
  introContent: { maxWidth: '800px', margin: '0 auto 4rem', textAlign: 'center' },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    color: '#1A1918',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  text: { color: '#585755', fontSize: '1.1rem', lineHeight: 1.8 },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '2rem',
  },
  benefitCard: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    padding: '2.5rem',
    textAlign: 'center',
  },
  benefitTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.25rem',
    color: '#1A1918',
    margin: '1.5rem 0 0.75rem',
  },
  benefitText: { color: '#585755', fontSize: '0.9rem', lineHeight: 1.6 },
  propertiesSection: { padding: '4rem 0' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '2rem',
    marginBottom: '3rem',
  },
  loadingText: { textAlign: 'center', color: '#8C8B88', padding: '3rem 0' },
  emptyText: { textAlign: 'center', color: '#8C8B88', padding: '3rem 0' },
  ctaCenter: { textAlign: 'center', marginTop: '3rem' },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '1rem 2rem',
    fontSize: '0.875rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: 500,
  },
};

export default CostaCalida;
